import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { ShieldCheck, Sparkles, FileText, TrendingUp, MapPin } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { adminApi, APP_STATUSES, REQ_STATUSES, STATUS_TONE } from "@/lib/adminApi";
import { AdminHeader } from "@/components/admin/AdminDashboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AiScanBlock({ app }) {
  const scan = app.ai_scan;
  const hasResume = !!app.resume;
  if (!hasResume && !scan) return null;
  const done = scan && scan.status === "done";
  return (
    <div className="mt-4 border-t border-white/10 pt-4" data-testid={`application-aiscan-${app.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 font-mono-tech text-[10px] tracking-[0.2em] text-[#FF5E1E] uppercase"><Sparkles size={13} /> Reentry AI Scan</div>
        {hasResume && (
          <a data-testid={`resume-download-${app.id}`} href={`${API}/admin/applications/${app.id}/resume`} target="_blank" rel="noreferrer"
            className="flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.12em] uppercase text-[#8FA3B8] hover:text-white border border-white/15 hover:border-[#FF5E1E]/60 px-2.5 py-1 transition-colors">
            <FileText size={12} /> {app.resume.original_filename || "Resume"}
          </a>
        )}
      </div>
      {!scan && <span className="font-mono-tech text-[10px] text-[#8FA3B8]">No resume attached.</span>}
      {scan && scan.status === "processing" && <span className="font-mono-tech text-[10px] text-[#FFB800]">Scan in progress…</span>}
      {scan && scan.status === "failed" && <span className="font-mono-tech text-[10px] text-[#8FA3B8]">Scan unavailable (couldn't read the file).</span>}
      {done && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-4">
            {scan.fit_score != null && <span className="font-display font-black text-2xl text-[#FF5E1E]">{scan.fit_score}<span className="text-xs text-[#8FA3B8] font-mono-tech ml-1">FIT</span></span>}
            {scan.top_trade && <span className="font-mono-tech text-[10px] tracking-[0.12em] uppercase text-[#FFB800]">Best fit: {scan.top_trade}</span>}
            {scan.potential_earnings?.top && <span className="flex items-center gap-1 font-mono-tech text-[10px] text-[#10B981]"><TrendingUp size={12} /> up to {scan.potential_earnings.top}</span>}
          </div>
          {scan.admin_summary && <p className="text-sm text-[#E4EAF0] bg-[#0B0F14] border-l-2 border-[#FF5E1E] pl-3 py-1.5">{scan.admin_summary}</p>}
          {scan.matched_jobs?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {scan.matched_jobs.map((j, i) => (
                <span key={i} className="font-mono-tech text-[9px] tracking-[0.1em] text-[#B8C4D0] border border-white/10 px-2 py-1 flex items-center gap-1">
                  {j.title}{j.location && <span className="text-[#8FA3B8] flex items-center gap-0.5"><MapPin size={9} /> {j.location}</span>}
                </span>
              ))}
            </div>
          )}
          {scan.personalized_message && (
            <details className="text-sm">
              <summary className="cursor-pointer font-mono-tech text-[9px] tracking-[0.15em] text-[#8FA3B8] uppercase hover:text-white">View candidate message</summary>
              <p className="mt-2 text-[#B8C4D0] leading-relaxed">{scan.personalized_message}</p>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function StatusSelect({ value, options, onChange, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className={`h-8 rounded-none border font-mono-tech text-[10px] tracking-[0.12em] uppercase w-[130px] ${STATUS_TONE[value] || "text-white border-white/20"}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-[#10161F] border-white/15 text-white rounded-none">
        {options.map((s) => <SelectItem key={s} value={s} className="focus:bg-[#FF5E1E] focus:text-[#0B0F14] font-mono-tech text-[11px] uppercase">{s}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function Empty({ text }) {
  return <div className="text-sm text-[#8FA3B8] border border-dashed border-white/10 p-8 text-center">{text}</div>;
}

function Shell({ children }) {
  return <div className="bg-[#10161F] border border-white/10 overflow-x-auto">{children}</div>;
}

// ---------------- Applications ----------------
export function AdminApplications() {
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => { setRows(await adminApi.applications()); }, []);
  useEffect(() => { load(); }, [load]);

  const update = async (id, status) => {
    try { await adminApi.updateApplication(id, status); setRows((r) => r.map((x) => (x.id === id ? { ...x, status } : x))); toast.success(`Application set to ${status}`); }
    catch { toast.error("Could not update status."); }
  };

  return (
    <div>
      <AdminHeader eyebrow="INTAKE — CANDIDATE APPLICATIONS" title="Applications" sub={`${rows.length} total submissions from the intake form.`} />
      {rows.length === 0 ? <Empty text="No candidate applications yet." /> : (
        <div className="space-y-3" data-testid="admin-applications-list">
          {rows.map((a) => (
            <div key={a.id} className="bg-[#10161F] border border-white/10 p-5" data-testid={`application-row-${a.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="font-display font-bold uppercase text-lg">{a.full_name}</div>
                  <div className="font-mono-tech text-[10px] tracking-[0.12em] text-[#8FA3B8] mt-1">{a.email} · {a.phone}</div>
                </div>
                <StatusSelect testid={`application-status-${a.id}`} value={a.status} options={APP_STATUSES} onChange={(s) => update(a.id, s)} />
              </div>
              <div className="mt-4 grid sm:grid-cols-4 gap-3 text-sm">
                <Field label="Target Track" value={a.target_track} />
                <Field label="Experience" value={a.experience_level} />
                <Field label="Location" value={a.preferred_location || "—"} />
                <Field label="Availability" value={a.availability || "—"} />
              </div>
              {a.message && <div className="mt-3 text-sm text-[#B8C4D0] border-t border-white/10 pt-3">"{a.message}"</div>}
              <AiScanBlock app={a} />
              <div className="font-mono-tech text-[9px] tracking-[0.12em] text-[#8FA3B8] mt-3">SUBMITTED {new Date(a.created_at).toLocaleString()}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------- Employer requests ----------------
export function AdminEmployerRequests() {
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => { setRows(await adminApi.employerRequests()); }, []);
  useEffect(() => { load(); }, [load]);

  const update = async (id, status) => {
    try { await adminApi.updateRequest(id, status); setRows((r) => r.map((x) => (x.id === id ? { ...x, status } : x))); toast.success(`Request set to ${status}`); }
    catch { toast.error("Could not update status."); }
  };

  return (
    <div>
      <AdminHeader eyebrow="DEMAND — EMPLOYER TALENT REQUESTS" title="Talent Requests" sub={`${rows.length} open + historical employer requests.`} />
      {rows.length === 0 ? <Empty text="No employer talent requests yet." /> : (
        <div className="space-y-3" data-testid="admin-requests-list">
          {rows.map((r) => (
            <div key={r.id} className="bg-[#10161F] border border-white/10 p-5" data-testid={`request-row-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="font-display font-bold uppercase text-lg">{r.company}</div>
                  <div className="font-mono-tech text-[10px] tracking-[0.12em] text-[#8FA3B8] mt-1">{r.contact_name} · {r.contact_email}</div>
                </div>
                <StatusSelect testid={`request-status-${r.id}`} value={r.status} options={REQ_STATUSES} onChange={(s) => update(r.id, s)} />
              </div>
              <div className="mt-4 grid sm:grid-cols-4 gap-3 text-sm">
                <Field label="Trade" value={r.trade} />
                <Field label="# Workers" value={String(r.num_workers)} />
                <Field label="Location" value={r.location || "—"} />
                <Field label="Shift" value={r.shift || "—"} />
              </div>
              {r.notes && <div className="mt-3 text-sm text-[#B8C4D0] border-t border-white/10 pt-3">{r.notes}</div>}
              <div className="font-mono-tech text-[9px] tracking-[0.12em] text-[#8FA3B8] mt-3">SUBMITTED {new Date(r.created_at).toLocaleString()}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------- Users ----------------
export function AdminUsers() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const load = useCallback(async () => { setRows(await adminApi.users()); }, []);
  useEffect(() => { load(); }, [load]);

  const shown = rows.filter((u) => filter === "all" || u.role === filter);

  return (
    <div>
      <AdminHeader
        eyebrow="ROSTER — REGISTERED USERS"
        title="Registered Users"
        sub={`${rows.length} accounts · track individual journey progress.`}
        right={
          <div className="flex gap-1">
            {["all", "candidate", "employer"].map((f) => (
              <button key={f} data-testid={`users-filter-${f}`} onClick={() => setFilter(f)} className={`px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.12em] uppercase border ${filter === f ? "bg-[#FF5E1E] text-[#0B0F14] border-[#FF5E1E]" : "border-white/15 text-[#8FA3B8] hover:text-white"}`}>{f}</button>
            ))}
          </div>
        }
      />
      {shown.length === 0 ? <Empty text="No users match this filter." /> : (
        <Shell>
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead>
              <tr className="font-mono-tech text-[9px] tracking-[0.14em] text-[#8FA3B8] uppercase border-b border-white/10">
                <th className="text-left p-4">Name</th>
                <th className="text-left p-4">Role</th>
                <th className="text-left p-4">Trade</th>
                <th className="text-left p-4 w-48">Journey</th>
                <th className="text-left p-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((u) => {
                const pct = Math.round(((u.journey_progress || 0) / 6) * 100);
                return (
                  <tr key={u.user_id} className="border-b border-white/5 hover:bg-white/5" data-testid={`user-row-${u.user_id}`}>
                    <td className="p-4"><div className="font-medium flex items-center gap-2">{u.name}{u.is_demo && <span className="font-mono-tech text-[8px] tracking-[0.1em] px-1.5 py-0.5 border border-white/20 text-[#8FA3B8] uppercase">Demo</span>}</div><div className="font-mono-tech text-[9px] text-[#8FA3B8]">{u.email}</div></td>
                    <td className="p-4"><span className="font-mono-tech text-[10px] tracking-[0.1em] uppercase text-[#B8C4D0]">{u.role}</span></td>
                    <td className="p-4 text-[#B8C4D0]">{u.trade || "—"}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-[#0B0F14] overflow-hidden"><div className="h-full bg-gradient-to-r from-[#FF5E1E] to-[#FFB800]" style={{ width: `${pct}%` }} /></div>
                        <span className="font-mono-tech text-[9px] text-[#8FA3B8] w-8">{pct}%</span>
                      </div>
                    </td>
                    <td className="p-4">
                      {u.workforce_ready
                        ? <span className="inline-flex items-center gap-1 font-mono-tech text-[9px] tracking-[0.1em] px-2 py-1 border border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]"><ShieldCheck size={11} /> READY</span>
                        : <span className="font-mono-tech text-[9px] tracking-[0.1em] text-[#8FA3B8]">IN PROGRESS</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Shell>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="font-mono-tech text-[9px] tracking-[0.14em] text-[#8FA3B8] uppercase">{label}</div>
      <div className="text-[#F4F6F8] mt-0.5">{value}</div>
    </div>
  );
}
